import {compact, max, partition} from 'lodash';
import React, {FC} from 'react';

import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {InboxModel} from '../../../../../../libs/shared/core/src/models/api/inboxModel';
import {useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {getCompanyPreferences} from '../../../../../store/data/company/companySelectors';
import {
  ConversationSummaryDetailsSubsection,
  StyledDetailsSubsectionListDiv,
} from './conversationSummaryDetailsSubsection';

/*
 * Props.
 */

interface ConversationSummaryRetentionProps {
  inboxes: ReadonlyArray<InboxModel>;
}

/*
 * Component.
 */

export const ConversationSummaryRetention: FC<ConversationSummaryRetentionProps> = ({inboxes}) => {
  const cutoffDays = useComputeConversationCutoffDays(inboxes);

  if (!cutoffDays) {
    return null;
  }

  return (
    <ConversationSummaryDetailsSubsection
      title={<Trans id="conversation-summary-retention-title">Data retention</Trans>}
      content={
        <StyledDetailsSubsectionListDiv>
          <Trans id="conversation-summary-retention-content">
            This conversation will be automatically deleted after {cutoffDays} days of inactivity
          </Trans>
        </StyledDetailsSubsectionListDiv>
      }
      paddingTop={26}
    />
  );
};

export function useComputeConversationCutoffDays(inboxes: ReadonlyArray<InboxModel>) {
  const {isDataRetentionEnabled, allowInboxLevelDataRetention, trashConversationsCutoffDays} =
    useAppSelector(getCompanyPreferences);

  if (inboxes.length === 0 || !isDataRetentionEnabled) {
    return undefined;
  }

  if (!allowInboxLevelDataRetention) {
    return trashConversationsCutoffDays;
  }

  const longestInboxCutoffDays = getLongestInboxCutoffDays(inboxes);
  if (longestInboxCutoffDays === 'never') {
    return undefined;
  }
  return longestInboxCutoffDays ?? trashConversationsCutoffDays;
}

function getLongestInboxCutoffDays(inboxes: ReadonlyArray<InboxModel>) {
  const [neverValues, customValues] = partition(
    compact(inboxes.map((inbox) => inbox.trashConversationsCutoffDays)),
    (i): i is 'never' => i === 'never',
  );
  if (neverValues.length > 0) {
    return 'never';
  }

  return max(customValues);
}
